import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CalendarMonthOutlined as ScheduleIcon,
  InboxOutlined as InboxIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import LoadingSkeleton from '../LoadingSkeleton';
import { APP_SHAPE, elevatedPanelSx } from '../../constants/shape';
import { useOrganizationStore } from '../../stores/organization.store';
import { useTruckStore } from '../../stores/transportation.store';
import { useOrganizationScheduleStore } from '../../stores/organizationSchedule.store';
import { getRouteColor } from '../../utils/routeColors';
import {
  findScheduleDayEntries,
  getCurrentWeekRange,
  getDateForDayInWeek,
  resolveScheduleDayLabelKey,
  SCHEDULE_DAY_DISPLAY_ORDER,
} from './truckRouteScheduleTab.helpers';
import { countTrucksWithNoRoutesThisWeek, joinTrucksWithSchedules } from './fleetScheduleMatrixTab.helpers';

const COLUMN_COUNT = SCHEDULE_DAY_DISPLAY_ORDER.length + 1;
const dateFormatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

/**
 * Fleet-wide weekly schedule matrix: `schedule` tab of `TransportationRoutes.tsx`
 * (Task 4). Rows = every truck in the org (name + plate identity cell),
 * columns = the 7 days (Sunday-first), cells = that truck's route(s) for
 * that day (or empty) -- a day can now hold 2+ ordered routes (Task 3), so a
 * cell with only one route still shows it directly (name + color dot,
 * clickable through to that route's own details), while a cell with several
 * shows a route count with a tooltip listing them in order; either way,
 * clicking through to edit still only happens from `TruckRouteScheduleTab.tsx`
 * itself (a single truck's own schedule tab), which this matrix's
 * identity-cell icon button (and, for a multi-route cell, the cell itself)
 * link out to. Read-only -- no per-cell editing here.
 *
 * Mounted with no props by `TransportationRoutes.tsx` only while its
 * `schedule` tab is active (a ternary that fully unmounts this component
 * when the `list` tab is active), mirroring how `TruckRouteScheduleTab`
 * only mounts while its own tab is active on `TruckDetails.tsx` -- so, like
 * that component, this one fetches on mount with a plain `useEffect` and no
 * extra "is this tab active" guard.
 */
export default function FleetScheduleMatrixTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const { trucks, trucksLoading, trucksError, fetchTrucks } = useTruckStore();
  const {
    schedules,
    isLoading: scheduleLoading,
    error: scheduleError,
    fetchOrganizationSchedule,
  } = useOrganizationScheduleStore();
  const [search, setSearch] = useState('');

  // Both fetches key off the org, same re-fetch-on-change shape as
  // `RoutesListTab.tsx` -- no extra stale-org guard beyond that.
  const orgId = selectedOrganization?.id;

  useEffect(() => {
    if (!orgId) return;
    void fetchTrucks({ organization_id: orgId });
    void fetchOrganizationSchedule(orgId);
  }, [orgId, fetchTrucks, fetchOrganizationSchedule]);

  // Every truck in the org gets a row, including ones with no schedule at
  // all (see `joinTrucksWithSchedules`) -- sorted by truck name, since
  // neither `trucks` nor `schedules` is guaranteed to arrive in a stable
  // order from the backend.
  const rows = useMemo(() => joinTrucksWithSchedules(trucks, schedules), [trucks, schedules]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(({ truck }) => {
      const haystack = [truck.name, truck.data?.en_plate_number, truck.data?.ar_plate_number]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [rows, search]);

  // Unaffected by the search box -- this is a whole-fleet stat, not a
  // count of what's currently visible.
  const noScheduleCount = useMemo(() => countTrucksWithNoRoutesThisWeek(rows), [rows]);

  if (!selectedOrganization) {
    return <Alert severity="info">{t('transportation.routes.selectOrganization')}</Alert>;
  }

  // Both fetches are equally required for any row to render correctly, so a
  // single retry re-fires both, same as the combined loading/error gates below.
  const retry = () => {
    void fetchTrucks({ organization_id: selectedOrganization.id });
    void fetchOrganizationSchedule(selectedOrganization.id);
  };

  const isLoading = trucksLoading || scheduleLoading;
  const error = trucksError || scheduleError;

  if (isLoading) {
    // `header={false}`: unlike `RoutesListTab.tsx`'s use of this same component,
    // this tab never renders its own `PageHeader` -- `TransportationRoutes.tsx`'s
    // shell already shows a real, permanent one above the tab panel, so the
    // default page-header-shaped skeleton would just be a redundant duplicate.
    return <LoadingSkeleton variant="table" header={false} filters={false} columns={COLUMN_COUNT} rows={6} />;
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" onClick={retry}>
            {t('common.retry')}
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  const today = SCHEDULE_DAY_DISPLAY_ORDER[new Date().getDay()];
  const todayCellSx = { bgcolor: alpha(theme.palette.primary.main, 0.06) } as const;
  // Presentation only, same recurring-template caveat as `TruckRouteScheduleTab.tsx`'s own
  // `getCurrentWeekRange` docstring -- this matrix has no date dimension either.
  const weekRange = getCurrentWeekRange();

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('transportation.routes.scheduleMatrix.searchPlaceholder')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 320, '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } }}
        />
        {rows.length > 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('transportation.routes.scheduleMatrix.summaryStat', { count: noScheduleCount, total: rows.length })}
          </Typography>
        ) : null}
      </Stack>

      <Paper elevation={2} sx={elevatedPanelSx}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
          <Table
            stickyHeader
            sx={{ '& .MuiTableCell-head': { py: 1.5, px: 2 }, '& .MuiTableCell-body': { py: 1.75, px: 2 } }}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('transportation.routes.columns.truck')}</TableCell>
                {SCHEDULE_DAY_DISPLAY_ORDER.map((day) => (
                  <TableCell key={day} align="center" sx={day === today ? todayCellSx : undefined}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block' }}
                    >
                      {t(resolveScheduleDayLabelKey(day))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dateFormatter.format(getDateForDayInWeek(weekRange.start, day))}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} sx={{ py: 8, border: 0 }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.text.secondary, 0.08),
                          color: 'text.secondary',
                        }}
                      >
                        <InboxIcon fontSize="large" />
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {search
                          ? t('transportation.routes.scheduleMatrix.noSearchResultsTitle')
                          : t('transportation.routes.scheduleMatrix.emptyTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {search
                          ? t('transportation.routes.scheduleMatrix.noSearchResultsHint')
                          : t('transportation.routes.scheduleMatrix.emptyHint')}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map(({ truck, days }) => (
                  <TableRow key={truck.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {truck.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {truck.data?.en_plate_number || truck.data?.ar_plate_number || '—'}
                          </Typography>
                        </Box>
                        <Tooltip
                          title={t('transportation.routes.scheduleMatrix.viewScheduleAriaLabel', {
                            truckName: truck.name,
                          })}
                        >
                          <IconButton
                            size="small"
                            aria-label={t('transportation.routes.scheduleMatrix.viewScheduleAriaLabel', {
                              truckName: truck.name,
                            })}
                            onClick={() => navigate(`/transportation/fleet/${truck.id}?tab=schedule`)}
                            sx={{ color: 'text.secondary', flexShrink: 0 }}
                          >
                            <ScheduleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    {SCHEDULE_DAY_DISPLAY_ORDER.map((day) => {
                      const entries = findScheduleDayEntries(days, day);
                      return (
                        <TableCell key={day} align="center" sx={day === today ? todayCellSx : undefined}>
                          {entries.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              &mdash;
                            </Typography>
                          ) : entries.length === 1 ? (
                            <ButtonBase
                              onClick={() => navigate(`/transportation/routes/${entries[0].route.id}`)}
                              title={entries[0].route.name}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                maxWidth: '100%',
                                px: 0.75,
                                py: 0.5,
                                borderRadius: 1,
                                // Explicit tint, not the theme's `action.hover` token -- see
                                // the same fix in `TruckRouteScheduleTab.tsx`.
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: getRouteColor(entries[0].route.id),
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="caption" noWrap sx={{ maxWidth: 108, minWidth: 0 }}>
                                {entries[0].route.name}
                              </Typography>
                            </ButtonBase>
                          ) : (
                            // 2+ routes this day (Task 3) -- no single route to link through to,
                            // so the whole cell links out to this truck's own schedule tab
                            // instead (same destination as the identity cell's icon button); the
                            // ordered route list itself is surfaced via the tooltip.
                            <Tooltip
                              title={
                                <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                                  {entries.map((entry, index) => `${index + 1}. ${entry.route.name}`).join('\n')}
                                </Box>
                              }
                            >
                              <ButtonBase
                                onClick={() => navigate(`/transportation/fleet/${truck.id}?tab=schedule`)}
                                aria-label={t('transportation.routes.scheduleMatrix.routeCount', {
                                  count: entries.length,
                                })}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  maxWidth: '100%',
                                  px: 0.75,
                                  py: 0.5,
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                                }}
                              >
                                <Stack direction="row" sx={{ flexShrink: 0 }}>
                                  {entries.slice(0, 3).map((entry, index) => (
                                    <Box
                                      key={entry.route.id}
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: getRouteColor(entry.route.id),
                                        border: '1px solid',
                                        borderColor: 'background.paper',
                                        ml: index === 0 ? 0 : -0.5,
                                      }}
                                    />
                                  ))}
                                </Stack>
                                <Typography variant="caption" noWrap sx={{ maxWidth: 108, minWidth: 0 }}>
                                  {t('transportation.routes.scheduleMatrix.routeCount', { count: entries.length })}
                                </Typography>
                              </ButtonBase>
                            </Tooltip>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
